import { FC, RefObject } from 'react';
import * as stylex from '@stylexjs/stylex';
import { ChevronDownSmallIcon } from '@shared/assets/icons';
import { styles } from '../../styles';
import { InvitationStatusTicketEnum } from '@pages/event/api/useFetchTicketSegment';
import { InvitationStatusDropdown } from '../invitation-status-dropdown';
import { getInviteStatusLabel } from '@features/event-participant-card/utils/invite-status';
import { UnstyledButton } from '@shared/ui/unstyled-button';

interface InvitationStatusBlockProps {
  ticket: {
    invitationStatus?: InvitationStatusTicketEnum | null;
    smsInviteSent?: boolean | null;
    emailInviteSent?: boolean | null;
  };
  isDropdownOpen: boolean;
  sendTicketAfterAcceptAt?: string | null;
  dropdownRef: RefObject<HTMLButtonElement>;
  onToggleDropdown: () => void;
  onCloseDropdown: () => void;
  onSelectAccepted: () => void;
  onSelectDeclined: () => void;
  eventDateEnd?: string;
}

export const InvitationStatusBlock: FC<InvitationStatusBlockProps> = ({
  ticket,
  isDropdownOpen,
  sendTicketAfterAcceptAt,
  dropdownRef,
  onToggleDropdown,
  onCloseDropdown,
  onSelectAccepted,
  onSelectDeclined,
  eventDateEnd,
}) => {
  const inviteStatus = ticket.invitationStatus ?? null;
  const isInviteAccepted = inviteStatus === InvitationStatusTicketEnum.ACCEPTED;
  const isInviteDeclined = inviteStatus === InvitationStatusTicketEnum.DECLINED;
  const isInviteSent = inviteStatus === InvitationStatusTicketEnum.SENT;
  const isInviteExpired = inviteStatus === InvitationStatusTicketEnum.EXPIRED;
  const showInviteStatus = isInviteAccepted || isInviteDeclined || isInviteSent || isInviteExpired;
  const inviteStatusLabel = getInviteStatusLabel(inviteStatus);

  if (!showInviteStatus) return null;

  const getTagStyle = () => {
    switch (inviteStatus) {
      case InvitationStatusTicketEnum.DECLINED:
        return styles.invitationStatusBlockWarning;
      case InvitationStatusTicketEnum.SENT:
        return styles.invitationStatusBlockSent;
    }
  };

  return (
    <div {...stylex.props(styles.invitationStatusContainer)}>
      {isInviteExpired ? (
        <div {...stylex.props(styles.invitationStatusExpired)}>
          <span {...stylex.props(styles.invitationStatus)}>{inviteStatusLabel}</span>
        </div>
      ) : (
        <>
          <UnstyledButton
            ref={dropdownRef}
            onClick={onToggleDropdown}
            {...stylex.props(styles.invitationStatusBlock, getTagStyle())}
          >
            <span {...stylex.props(styles.invitationStatus)}>{inviteStatusLabel}</span>

            <div {...stylex.props(styles.invitationStatusIcon)}>
              <ChevronDownSmallIcon width="12" height="12" />
            </div>
          </UnstyledButton>

          <InvitationStatusDropdown
            anchorRef={dropdownRef}
            isInviteAccepted={isInviteAccepted}
            isInviteDeclined={isInviteDeclined}
            isDropdownOpen={isDropdownOpen}
            onClose={onCloseDropdown}
            onSelectAccepted={onSelectAccepted}
            onSelectDeclined={onSelectDeclined}
            eventDateEnd={eventDateEnd}
            sendTicketAfterAcceptAt={sendTicketAfterAcceptAt}
          />
        </>
      )}
    </div>
  );
};
